"use client";

import { useCallback, useEffect, useRef } from "react";
import {
  createImpressionBatcher,
  IMPRESSION_VISIBLE_RATIO,
  type ImpressionBatcher,
} from "@/lib/analytics/card-impressions";

const PRODUCT_ID_ATTRIBUTE = "data-impression-id";
const POSITION_ATTRIBUTE = "data-impression-position";

interface ImpressionTracking {
  observer: IntersectionObserver;
  batcher: ImpressionBatcher;
}

function startTracking(list: string): ImpressionTracking | null {
  if (typeof IntersectionObserver === "undefined") return null;
  const batcher = createImpressionBatcher(list);
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.intersectionRatio < IMPRESSION_VISIBLE_RATIO) continue;
        const productId = entry.target.getAttribute(PRODUCT_ID_ATTRIBUTE);
        if (!productId) continue;
        batcher.seen({ productId, position: Number(entry.target.getAttribute(POSITION_ATTRIBUTE)) });
      }
    },
    { threshold: IMPRESSION_VISIBLE_RATIO },
  );
  return { observer, batcher };
}

export function useCardImpressions(list: string) {
  const tracking = useRef<ImpressionTracking | null>(null);

  const current = useCallback(() => {
    tracking.current ??= startTracking(list);
    return tracking.current;
  }, [list]);

  useEffect(() => {
    const flushWhenHidden = () => {
      if (document.visibilityState === "hidden") tracking.current?.batcher.flush();
    };
    document.addEventListener("visibilitychange", flushWhenHidden);
    return () => {
      document.removeEventListener("visibilitychange", flushWhenHidden);
      tracking.current?.observer.disconnect();
      tracking.current?.batcher.flush();
      tracking.current = null;
    };
  }, [list]);

  return useCallback((productId: string, position: number) => ({
    [PRODUCT_ID_ATTRIBUTE]: productId,
    [POSITION_ATTRIBUTE]: position,
    ref: (element: HTMLElement | null) => {
      const active = element ? current() : null;
      if (!element || !active) return;
      active.observer.observe(element);
      return () => active.observer.unobserve(element);
    },
  }), [current]);
}
